import { Editor } from '@tiptap/react';
import { Node as ProseMirrorNode } from '@tiptap/pm/model';

function personaIdOf(node: ProseMirrorNode): string {
  return typeof node.attrs.personaId === 'string' ? node.attrs.personaId : '';
}

// -- unsetBlockPersona ---------------------------------------------------------
// Reverts the top-level block at `pos` to the persona of the block above so it
// rejoins that run. Personas are stored as a concrete id on every block (there
// is no true "inherit"), so a later block that only carried this persona over
// from above holds its own copy of the same id. Reverting just this block would
// strand those followers as a fresh run, so we also rewrite the contiguous run
// of following blocks that share the old id. The whole change is one
// transaction, so it is a single undoable step and one canvas re-render.
export function unsetBlockPersona(editor: Editor, pos: number): void {
  const { state } = editor.view;
  const $pos = state.doc.resolve(pos);
  if ($pos.depth !== 0) {
    return;
  }
  const index = $pos.index(0);
  if (index === 0) {
    return;
  }
  const current = state.doc.maybeChild(index);
  const previous = state.doc.maybeChild(index - 1);
  if (!current || !previous) {
    return;
  }
  const oldId = personaIdOf(current);
  const newId = personaIdOf(previous);
  if (newId === oldId) {
    return;
  }

  // Collect the start offset of each top-level block up front, since rewriting
  // attributes in a forEach would shift offsets as we go.
  const offsets: number[] = [];
  state.doc.forEach((_child, childOffset) => offsets.push(childOffset));

  const tr = state.tr;
  // This block, plus the contiguous following run that shares its old id (those
  // are the followers that carried it over). Stop at the first block that does
  // not, so a later, separate run with the same id is left untouched.
  for (let i = index; i < state.doc.childCount; i++) {
    const block = state.doc.maybeChild(i);
    const offset = offsets[i];
    if (!block || offset === undefined || personaIdOf(block) !== oldId) {
      break;
    }
    tr.setNodeAttribute(offset, 'personaId', newId);
  }
  if (tr.docChanged) {
    editor.view.dispatch(tr);
  }
}
